import { useRef, useState } from 'react';
import { DotsSixVertical, X, Check } from '@phosphor-icons/react';
import telegramService from '../../services/telegram';

/**
 * Список мест маршрута: перетаскивание за ручку и удаление.
 *
 * Тянется только за ручку слева (`touch-action: none` на ней одной):
 * вся строка жест не перехватывает, и список, и панель вокруг
 * прокручиваются как обычно. Внутри Telegram перетаскивание всей
 * строкой спорило бы с прокруткой — так решили на анализе.
 *
 * - `items` — `[{ key, title, thumb, subtitle }]`;
 * - `onReorder(from, to)`, `onRemove(index)`;
 * - `lockedCount` — первые места, которые уже пройдены: их не двигают,
 *   не удаляют, и выше них никого не ставят;
 * - `minCount` — меньше этого убрать нельзя (крестик скрыт).
 */
const SortableStopList = ({ items, onReorder, onRemove, lockedCount = 0, minCount = 2 }) => {
  // { index, dy, h } — что тянем и насколько
  const [drag, setDrag] = useState(null);
  const startRef = useRef(null);

  const targetOf = (d) => {
    if (!d) return null;
    const t = d.index + Math.round(d.dy / d.h);
    return Math.max(lockedCount, Math.min(items.length - 1, t));
  };
  const target = targetOf(drag);

  const onHandleDown = (e, index) => {
    if (index < lockedCount) return;
    e.preventDefault();
    const row = e.currentTarget.closest('li');
    // Строки одной высоты — шаг сдвига берём по той, что тянем,
    // плюс зазор между строками
    const h = row.offsetHeight + 8;
    startRef.current = { y: e.clientY, index, h };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    telegramService.hapticFeedback('light');
    setDrag({ index, dy: 0, h });
  };
  const onHandleMove = (e) => {
    const s = startRef.current;
    if (!s) return;
    s.dy = e.clientY - s.y;
    setDrag({ index: s.index, dy: s.dy, h: s.h });
  };
  const onHandleUp = () => {
    const s = startRef.current;
    startRef.current = null;
    setDrag(null);
    if (!s) return;
    const to = targetOf({ index: s.index, dy: s.dy || 0, h: s.h });
    if (to !== null && to !== s.index) {
      telegramService.hapticFeedback('light');
      onReorder(s.index, to);
    }
  };

  // Сдвиг соседей, пока строка едет: освобождаем место под неё
  const shiftOf = (i) => {
    if (!drag || i === drag.index || target === null) return 0;
    if (drag.index < target && i > drag.index && i <= target) return -drag.h;
    if (drag.index > target && i < drag.index && i >= target) return drag.h;
    return 0;
  };

  return (
    <ol className="m-0 flex list-none flex-col gap-2 p-0">
      {items.map((item, i) => {
        const locked = i < lockedCount;
        const dragging = drag?.index === i;
        return (
          <li
            key={item.key}
            className={`relative flex min-h-[64px] items-center gap-3 rounded-lg border bg-theme-card px-2 py-2 ${
              dragging ? 'z-10 border-action' : 'border-theme-border'}`}
            style={{
              transform: `translateY(${dragging ? drag.dy : shiftOf(i)}px)`,
              transition: dragging ? 'none' : 'transform var(--dur-fast, 160ms) cubic-bezier(.2,0,0,1)',
            }}
          >
            {/* Ручка: 44px тап-зона при иконке 20px (брендбук 9.3) */}
            {locked ? (
              <span
                className="flex h-11 w-8 shrink-0 items-center justify-center text-theme-text-hint"
                aria-label="Пройдено"
              >
                <Check size={18} weight="bold" />
              </span>
            ) : (
              <span
                onPointerDown={(e) => onHandleDown(e, i)}
                onPointerMove={onHandleMove}
                onPointerUp={onHandleUp}
                onPointerCancel={onHandleUp}
                role="button"
                aria-label={`Перетащить «${item.title}»`}
                className="flex h-11 w-8 shrink-0 cursor-grab items-center justify-center text-theme-text-hint"
                style={{ touchAction: 'none' }}
              >
                <DotsSixVertical size={20} weight="bold" />
              </span>
            )}
            <span
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-kicker tabular"
              style={locked
                ? { background: 'var(--color-text-hint)', color: 'var(--color-card)' }
                : { background: 'var(--color-action)', color: 'var(--color-on-action)' }}
            >
              {i + 1}
            </span>
            <div
              className="w-9 shrink-0 overflow-hidden rounded-md bg-theme-card-secondary"
              style={{ aspectRatio: '3 / 4' }}
            >
              {item.thumb && <img src={item.thumb} alt="" className="h-full w-full object-cover" draggable={false} />}
            </div>
            <div className="min-w-0 flex-1">
              <div className={`truncate text-body ${locked ? 'text-theme-text-secondary' : 'text-theme-text-primary'}`}>
                {item.title}
              </div>
              {item.subtitle && (
                <div className="truncate text-caption text-theme-text-secondary">{item.subtitle}</div>
              )}
            </div>
            {!locked && items.length > minCount && (
              <button
                onClick={() => { telegramService.hapticFeedback('light'); onRemove(i); }}
                aria-label={`Убрать «${item.title}»`}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-0 bg-transparent text-theme-text-secondary"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <X size={20} />
              </button>
            )}
          </li>
        );
      })}
    </ol>
  );
};

export default SortableStopList;
